import cn from "classnames"
import { forwardRef, useEffect, useRef, useState } from "react"

import { Button } from "@/atoms"
import { useResizeObserver } from "@/hooks/useResizeObserver"
import { CloseCircleFilled } from "@/icons"
import {
  APPLY_BUTTON_SMART_SEARCH_ID,
  INPUT_SMART_SEARCH_ID
} from "@/organisms/SmartSearch/const/symbol.constants"
import { getEnterEvent } from "@/organisms/SmartSearch/helpers/event.helper"
import {
  CONTAINER_SELECTOR,
  FILTER_ATTRIBUTE_NAME,
  LOADING_VALUE
} from "@/templates/TabTemplate"
import {
  resetStyleForMinimizeSpanList,
  setStyleForMinimizeSpanList
} from "./helpers"

import styles from "./InputSmartSearch.module.scss"

export const InputSmartSearch = forwardRef<HTMLDivElement, any>(
  (
    {
      refInput,
      open,
      html,
      placeholder,
      onlySearch,
      showPlaceholder,
      handlers,
      disabled,
      dirty
    }: any,
    ref
  ) => {
    const [state, setState] = useState({
      countsHiddenElements: 0,
      resize: 0
    })
    const { countsHiddenElements, resize } = state
    const rerender = () =>
      setState((prevState) => ({ ...prevState, resize: Date.now() }))

    useEffect(() => {
      if (open || !!resize) resetStyleForMinimizeSpanList(refInput)
    }, [resize, open, refInput])

    useResizeObserver(refInput, rerender)

    useEffect(() => {
      if (open) return

      const { countsHiddenElements } = setStyleForMinimizeSpanList(refInput)
      const { clientWidth, clientHeight } = refInput?.current || {}
      const isDisplayNone = !clientWidth && !clientHeight

      if (isDisplayNone) return

      setState((prevState) => ({ ...prevState, countsHiddenElements }))
    }, [open, html, countsHiddenElements, resize, refInput])

    useEffect(() => {
      const event = "paste"
      const listener = (event: ClipboardEvent) => {
        const text = event.clipboardData?.getData("text/plain")

        if (!text) event.preventDefault()
      }

      const input = refInput?.current

      input?.addEventListener(event, listener)

      return () => {
        input?.removeEventListener(event, listener)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <div
        ref={ref}
        className={cn(
          styles.container,
          open && styles.focus,
          disabled && styles.disabled
        )}
      >
        <div
          data-id-smart-search={INPUT_SMART_SEARCH_ID}
          data-placeholder={showPlaceholder ? placeholder : undefined}
          ref={refInput}
          contentEditable={!disabled && "plaintext-only"}
          suppressContentEditableWarning
          spellCheck={false}
          className={cn(
            styles.input,
            showPlaceholder && styles.placeholder,
            open && styles.open
          )}
          dangerouslySetInnerHTML={{
            __html: html
          }}
          onKeyUp={handlers.onKeyUp}
          onKeyDown={handlers.onKeyDown}
          onInput={handlers.onInput}
          onFocus={handlers.onFocus}
          onClick={handlers.onClick}
          onDoubleClick={(event) => {
            const target = event.target as HTMLDivElement

            if (target) window.getSelection()?.selectAllChildren(target)
          }}
          onPaste={handlers.onPaste}
        />
        {!!countsHiddenElements && !open && (
          <div className={styles.count}>
            <span>{`+${countsHiddenElements}`}</span>
          </div>
        )}
        {!showPlaceholder && (
          <div className={cn(styles.iconClear, "clear-button-smart-search")}>
            <Button
              type="text"
              icon={<CloseCircleFilled />}
              onClick={handlers.onClear}
            />
          </div>
        )}
        <div className={cn(styles.iconSearch, "search-button-smart-search")}>
          <Button
            id={APPLY_BUTTON_SMART_SEARCH_ID}
            disabled={!dirty}
            type="primary"
            onClick={() => {
              refInput.current?.dispatchEvent(getEnterEvent())
            }}
          >
            {onlySearch ? "Search" : "Apply"}
          </Button>
        </div>
      </div>
    )
  }
)

InputSmartSearch.displayName = "InputSmartSearch"

export const InputSmartSearchLoading = ({
  placeholder
}: {
  placeholder?: string
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current?.closest(CONTAINER_SELECTOR)

    element?.setAttribute?.(FILTER_ATTRIBUTE_NAME, LOADING_VALUE)
  }, [])

  return (
    <InputSmartSearch
      disabled
      showPlaceholder
      ref={ref}
      placeholder={placeholder}
      handlers={{}}
    />
  )
}
